import { computed, inject, Injectable, signal } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { APP_CONFIGURATION_TOKEN, DateOnly, LoaderService, PatientTreatmentSettings, Plan, TreatmentPlanSettings, TreatmentStatusType } from '../core';
import { PatientSettingsForm } from '../models';
import { PatientService } from './patient.service';
import { PlanService } from './plan.service';
import { TreatmentService } from './treatment.service';


/**
 * This service handles publish page data.
 */
@Injectable({ providedIn: 'root' })
export class PublishService {

	private readonly planSrv = inject(PlanService);
	private readonly patSrv = inject(PatientService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly treatSrv = inject(TreatmentService);
	private readonly configs = inject(APP_CONFIGURATION_TOKEN);

	plan = signal<Plan | undefined>(undefined);

	planSettings = signal<TreatmentPlanSettings | null>(null);
	treatmentProgress = computed(() => this.treatSrv.treatmentProgressMapper(this.planSettings(), 90));
	consolidationDays = signal<number | undefined>(undefined);
	isConsolidationValid = signal<boolean>(false);
	threshold = signal<number>(0);
	publishedPlans = signal<TreatmentPlanSettings[] | null>(null);

	patSettings = signal<PatientSettingsForm | null>(null);
	isContactInfoValid = signal<boolean>(false);
	isPinSiteCareValid = signal<boolean>(false);
	isMotMessagesValid = signal<boolean>(false);

	isPlanValid = computed(() => this.isConsolidationValid());
	isPatientValid = computed(() =>
		this.isContactInfoValid() &&
		this.isPinSiteCareValid() &&
		this.isMotMessagesValid()
	);

	publishStatus = signal<boolean | null>(null, { equal: (a, b) => false });


	/**
	 * Init publish page data.
	 */
	init(planId: string): Observable<boolean> {
		this.loaderSrv.show();
		return this.planSrv.getPlan(planId).pipe(
			switchMap(plan => !plan || plan.isPublished || plan.isRevoked ? throwError(() => new Error('Plan not valid')) : of(plan)),
			tap(plan => this.plan.set(plan)),
			switchMap(plan => this.treatSrv.getTreatmentPlan(planId, plan.userGuid)),
			switchMap(planStgs => this._isInitialPlanInvalid(planStgs) ? throwError(() => new Error('Plan not valid')) : of(planStgs)),
			tap(planStgs => this.planSettings.set(planStgs)),
			switchMap(_ => forkJoin([
				this.treatSrv.getPublishedPlansProgress(this.plan()!.patient.id, this.plan()!.userGuid),
				this.patSrv.getPatSettings(this.plan()!.patient.id, this.plan()!.userGuid)
			])),
			tap(([plans, patStgs]) => {
				this.publishedPlans.set(plans);
				this.patSettings.set(this.patSrv.patSettingsFormMapper(patStgs));
			}),
			finalize(() => this.loaderSrv.hide()),
			map(() => true)
		);
	}

	private _isInitialPlanInvalid(planStgs: TreatmentPlanSettings | null): boolean {
		return planStgs?.status !== TreatmentStatusType.DRAFTED;
	}

	/**
	 * Publish treatment plan.
	 */
	publish(): void {
		this.loaderSrv.show();
		const treatment = this._treatmentPublish;
		this.treatSrv.publish(this.plan()!.id, treatment).pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: () => this.publishStatus.set(true),
			error: () => this.publishStatus.set(false)
		});
	}

	private get _treatmentPublish(): PatientTreatmentSettings {
		return {
			patient: {
				email: this.patSettings()!.email,
				secondEmail: this.patSettings()!.secondEmail,
				pinSiteCare: {
					enable: !!this.patSettings()!.pinSiteCareEnable,
					firstDay: this.patSettings()!.firstDay ? new DateOnly(this.patSettings()!.firstDay!) : undefined,
					frequency: this.patSettings()!.frequency!
				},
				motivationalMessage: {
					enable: !!this.patSettings()!.mmEnable,
					enabledSet: this.patSettings()!.enabledSet?.length ? this.patSettings()!.enabledSet : undefined,
					customSet: this.patSettings()!.customSet
				}
			},
			treatment: {
				consolidationDays: this.consolidationDays()!,
				daysBeforeAlert: this.threshold()
			}
		};
	}

	/**
	 * Redirect to tlhex site.
	 */
	goToTlhex(planId: string): void {
		this.loaderSrv.show();
		setTimeout(() => window.location.href = `${this.configs.tlhexSite}${planId}`, 3000);
	}

	/**
	 * Redirect to plan list site.
	 */
	goToPlanList(): void {
		this.loaderSrv.show();
		setTimeout(() => window.location.href = `${this.configs.planListSite}`, 3000);
	}

	/**
	 * Reset publish page data.
	 */
	reset(): void {
		this.plan.set(undefined);
		this.planSettings.set(null);
		this.consolidationDays.set(undefined);
		this.isConsolidationValid.set(false);
		this.threshold.set(0);
		this.publishedPlans.set(null);
		this.patSettings.set(null);
		this.isContactInfoValid.set(false);
		this.isPinSiteCareValid.set(false);
		this.isMotMessagesValid.set(false);
		this.publishStatus.set(null);
	}
}